/**
 * `Retry-After` parsing, shared by every retry policy that talks to the exchange API
 * (`fetchServerSide`'s build-phase backoff, the midterms hub's rate-limit retry).
 *
 * The header comes in two RFC 9110 shapes — delta-seconds (`Retry-After: 5`) and an
 * HTTP-date (`Retry-After: Wed, 21 Oct 2026 07:28:00 GMT`) — and upstreams send both.
 * Callers decide the fallback when it is absent, so this returns null rather than guessing.
 */
export function parseRetryAfterMs(response: Response): number | null {
    const value = response.headers.get('retry-after')?.trim();
    if (!value) return null;

    // RFC 9110 defines delta-seconds as digits only.
    if (/^\d+$/.test(value)) return Number(value) * 1_000;

    // Numeric but not digits-only (negative, decimal, exponent) is malformed. Bail out here
    // rather than falling through: V8's legacy date parsing reads '-5' as the year 2001, which
    // clamps to 0 below and would turn a bad header into "retry immediately".
    if (Number.isFinite(Number(value))) return null;

    const date = Date.parse(value);
    if (!Number.isNaN(date)) return Math.max(0, date - Date.now());

    return null;
}
